import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Message,
    Loader,
    Icon,
    Confirm,
    Label,
    Checkbox
} from 'semantic-ui-react';
import { api } from '../api/apiClient';
import { useTablePreferences } from '../hooks/useTablePreferences';
import { useDropdownOptions } from '../hooks/useDropdownOptions';
import PaginationFooter from './PaginationFooter';

const AdminPanel = () => {
    const [allUsers, setAllUsers] = useState([]); // Store all users for filtering
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const errorRef = useRef(null);
    const [success, setSuccess] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRoleFilter, setSelectedRoleFilter] = useState(null); // For filtering by role
    // Modal states
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    
    // Default form state helper
    const createEmptyFormData = () => ({
        email: '',
        roles: [],
        categoriesSingleTenancy: [],
        categoriesMultiTenancy: [],
        approverCategoriesSingleTenancy: [],
        approverCategoriesMultiTenancy: [],
        approverCategoriesSTEnabled: false,
        approverCategoriesMTEnabled: false,
        linodeEnabled: false,
        preReleaseAccessEnabled: false,
        autoProvisionEnabled: false, // Main toggle for UI
        autoProvisionSingleTenancyEnabled: false,
        autoProvisionSingleTenancyCategories: [],
        autoProvisionMultiTenancyEnabled: false,
        autoProvisionMultiTenancyCategories: []
    });

    // Form states
    const [formData, setFormData] = useState(createEmptyFormData());
    const [editingEmail, setEditingEmail] = useState('');
    const [deletingEmail, setDeletingEmail] = useState('');
    const [emailExists, setEmailExists] = useState(false);
    const [emailValidationMessage, setEmailValidationMessage] = useState('');
    
    // Use reusable hook for table preferences
    const { rowsPerPage, tableDensity, activePage, setActivePage } = useTablePreferences();
    
    const { options: roleOptions } = useDropdownOptions('roles');
    const availableRoles = roleOptions.map(option => option.value);

    const getRoleDisplayLabel = (role) => {
        return roleOptions.find(option => option.value === role)?.text || role;
    };
    const { options: singleTenancyCategoryOptions } = useDropdownOptions('single_tenancy_categories');
    const { options: multiTenancyCategoryOptions } = useDropdownOptions('multi_tenancy_categories');
    const [availableCategories, setAvailableCategories] = useState([]);
    const validMultiTenancyCategories = multiTenancyCategoryOptions.map(option => option.value);
    
    // Helper to get display name for MT category (PROD shows as "PROD / POC")
    const getMTCategoryDisplayName = (category) => {
        return multiTenancyCategoryOptions.find(option => option.value === category)?.text || category;
    };

    // Helper function to reset email validation state
    const resetEmailValidation = () => {
        setEmailExists(false);
        setEmailValidationMessage('');
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const allCategories = new Set(singleTenancyCategoryOptions.map(option => option.value));
        allUsers.forEach(user => {
            // Legacy categories
            if (user.categories && Array.isArray(user.categories)) {
                user.categories.forEach(cat => {
                    if (cat && cat.trim()) allCategories.add(cat.trim());
                });
            }
            // Single Tenancy categories
            if (user.categoriesSingleTenancy && Array.isArray(user.categoriesSingleTenancy)) {
                user.categoriesSingleTenancy.forEach(cat => {
                    if (cat && cat.trim()) allCategories.add(cat.trim());
                });
            }
            // Multi Tenancy categories (store as PROD, not PROD / POC for internal use)
            if (user.categoriesMultiTenancy && Array.isArray(user.categoriesMultiTenancy)) {
                user.categoriesMultiTenancy.forEach(cat => {
                    if (cat && cat.trim()) allCategories.add(cat.trim());
                });
            }
        });
        setAvailableCategories(Array.from(allCategories).sort());
    }, [allUsers, singleTenancyCategoryOptions]);

    // Check if email already exists when user types
    useEffect(() => {
        if (!addModalOpen) {
            resetEmailValidation();
            return;
        }

        const email = formData.email?.trim();
        
        // Reset validation when email is empty
        if (!email) {
            resetEmailValidation();
            return;
        }

        // Check if email format is valid
        const isValidFormat = email.includes('@crashplan.com') && email.length > '@crashplan.com'.length;
        
        if (!isValidFormat) {
            resetEmailValidation();
            return;
        }

        // Check if email exists in allUsers (case-insensitive)
        const emailLower = email.toLowerCase();
        const existingUser = allUsers.find(user => 
            user.email && user.email.toLowerCase() === emailLower
        );

        if (existingUser) {
            setEmailExists(true);
            const roles = existingUser.roles && existingUser.roles.length > 0 
                ? existingUser.roles.join(', ') 
                : 'No roles';
            const categories = existingUser.categories && existingUser.categories.length > 0
                ? existingUser.categories.join(', ')
                : 'No categories';
            setEmailValidationMessage(
                `User already exists with roles: ${roles} and categories: ${categories}`
            );
        } else {
            resetEmailValidation();
        }
    }, [formData.email, allUsers, addModalOpen]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.admin.getUsers();
            if (response.data && response.data.success) {
                const fetchedUsers = response.data.users || [];
                // Deduplicate users by email (case-insensitive) - keep first occurrence
                const uniqueUsers = [];
                const seenEmails = new Set();
                fetchedUsers.forEach(user => {
                    const emailLower = user.email?.toLowerCase();
                    if (emailLower && !seenEmails.has(emailLower)) {
                        seenEmails.add(emailLower);
                        uniqueUsers.push(user);
                    } else if (emailLower && seenEmails.has(emailLower)) {
                        console.warn(`Duplicate user detected: ${user.email}. Keeping first occurrence.`);
                    }
                });
                setAllUsers(uniqueUsers);
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.response?.data?.message || 'Failed to fetch users');
            setAllUsers([]); // Clear all users on error
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async () => {
        try {
            setError(null);
            setSuccess(null);
            if (!formData.email || !formData.email.includes('@crashplan.com')) {
                setError('Valid @crashplan.com email is required');
                return;
            }

            // Check if user already exists before attempting to add
            if (emailExists) {
                setError('This user already exists. Please use the Edit button to modify their roles or categories.');
                return;
            }

            // Filter out SUB_APPROVER from roles before adding
            const filteredRoles = formData.roles.filter(role => role !== 'SUB_APPROVER');

            // Validate that APPROVER role has at least one approver category enabled (ST or MT)
            if (filteredRoles.includes('APPROVER')) {
                const stEnabled = !!formData.approverCategoriesSTEnabled;
                const mtEnabled = !!formData.approverCategoriesMTEnabled;
                const stSelected = Array.isArray(formData.approverCategoriesSingleTenancy) && formData.approverCategoriesSingleTenancy.length > 0;
                const mtSelected = Array.isArray(formData.approverCategoriesMultiTenancy) && formData.approverCategoriesMultiTenancy.length > 0;

                // At least one tenancy must be enabled
                if (!stEnabled && !mtEnabled) {
                    setError('At least one approver category (Single Tenancy or Multi Tenancy) must be enabled for an approver. Approvers can only see requests matching their assigned categories.');
                    return;
                }

                // If ST is enabled, categories must be selected
                if (stEnabled && !stSelected) {
                    setError('At least one Single Tenancy category must be selected when Single Tenancy approver categories are enabled.');
                    return;
                }

                // If MT is enabled, categories must be selected
                if (mtEnabled && !mtSelected) {
                    setError('At least one Multi Tenancy category must be selected when Multi Tenancy approver categories are enabled.');
                    return;
                }
            }

            // Validate that ST_FORM_FILLER has ST categories selected
            if (filteredRoles.includes('ST_FORM_FILLER')) {
                const hasSTCategories = Array.isArray(formData.categoriesSingleTenancy) && formData.categoriesSingleTenancy.length > 0;
                if (!hasSTCategories) {
                    setError('Single Tenancy categories are required when the ST_FORM_FILLER role is enabled. Please select at least one Single Tenancy category.');
                    return;
                }
            }

            // Validate that MT_FORM_FILLER has MT categories selected
            if (filteredRoles.includes('MT_FORM_FILLER')) {
                const hasMTCategories = Array.isArray(formData.categoriesMultiTenancy) && formData.categoriesMultiTenancy.length > 0;
                if (!hasMTCategories) {
                    setError('Multi Tenancy categories are required when the MT_FORM_FILLER role is enabled. Please select at least one Multi Tenancy category.');
                    return;
                }
            }

            // Validate auto provisioning categories when enabled
            const autoProvisionSTEnabled = !!formData.autoProvisionSingleTenancyEnabled;
            const autoProvisionMTEnabled = !!formData.autoProvisionMultiTenancyEnabled;
            const autoProvisionSTSelected = Array.isArray(formData.autoProvisionSingleTenancyCategories) && formData.autoProvisionSingleTenancyCategories.length > 0;
            const autoProvisionMTSelected = Array.isArray(formData.autoProvisionMultiTenancyCategories) && formData.autoProvisionMultiTenancyCategories.length > 0;

            // If auto provisioning ST is enabled, categories must be selected
            if (autoProvisionSTEnabled && !autoProvisionSTSelected) {
                setError('At least one Single Tenancy category must be selected when auto provisioning for Single Tenancy is enabled.');
                return;
            }

            // If auto provisioning MT is enabled, categories must be selected
            if (autoProvisionMTEnabled && !autoProvisionMTSelected) {
                setError('At least one Multi Tenancy category must be selected when auto provisioning for Multi Tenancy is enabled.');
                return;
            }

            // Use categories from form data, no auto-population for DEVOPS
            const categoriesST = Array.isArray(formData.categoriesSingleTenancy) ? formData.categoriesSingleTenancy : [];
            const categoriesMT = Array.isArray(formData.categoriesMultiTenancy) ? formData.categoriesMultiTenancy : [];
            // Only assign approver categories if the toggle is enabled
            const approverCategoriesST = formData.approverCategoriesSTEnabled
                ? (Array.isArray(formData.approverCategoriesSingleTenancy) ? formData.approverCategoriesSingleTenancy : [])
                : [];
            const approverCategoriesMT = formData.approverCategoriesMTEnabled
                ? (Array.isArray(formData.approverCategoriesMultiTenancy) ? formData.approverCategoriesMultiTenancy : [])
                : [];

            // Ensure all arrays are properly formatted (even if empty)
            const ensureArray = (arr) => {
                if (!arr) return [];
                if (Array.isArray(arr)) return arr.filter(c => c && c.trim());
                return [];
            };

            const response = await api.admin.addUser(
                formData.email,
                filteredRoles,
                ensureArray(categoriesST),
                ensureArray(categoriesMT),
                ensureArray(approverCategoriesST),
                ensureArray(approverCategoriesMT),
                formData.autoProvisionSingleTenancyEnabled || false,
                ensureArray(formData.autoProvisionSingleTenancyCategories),
                formData.autoProvisionMultiTenancyEnabled || false,
                ensureArray(formData.autoProvisionMultiTenancyCategories),
                !!formData.linodeEnabled
            );
            if (response.data && response.data.success) {
                setSuccess('User added successfully');
                setAddModalOpen(false);
                setFormData(createEmptyFormData());
                // Add small delay to ensure backend has processed the insert
                await new Promise(resolve => setTimeout(resolve, 300));
                // Refresh the user list to show the new user
                setLoading(true);
                try {
                    await fetchUsers();
                } catch (fetchError) {
                    console.error('Error refreshing user list:', fetchError);
                }
                setTimeout(() => setSuccess(null), 3000);
            } else {
                throw new Error(response.data?.message || 'Failed to add user');
            }
        } catch (err) {
            console.error('Error adding user:', err);
            setError(err.response?.data?.message || err.message || 'Failed to add user');
        }
    };

    const handleEditUser = (user) => {
        setEditingEmail(user.email);
        const hasAutoProvision = !!(user.autoProvisionSingleTenancyEnabled || user.autoProvisionMultiTenancyEnabled);
        
        // Helper to safely get array from user data
        const safeArray = (arr) => {
            if (!arr) return [];
            if (Array.isArray(arr)) return [...arr];
            return [];
        };
        
        const userRoles = safeArray(user.roles).filter(role => role !== 'SUB_APPROVER'); // Filter out deprecated SUB_APPROVER role
        
        // Set form data directly from user data without any auto-enabling logic
        let formDataToSet = {
            email: user.email || '',
            roles: userRoles,
            categoriesSingleTenancy: safeArray(user.categoriesSingleTenancy),
            categoriesMultiTenancy: safeArray(user.categoriesMultiTenancy),
            approverCategoriesSingleTenancy: safeArray(user.approverCategoriesSingleTenancy),
            approverCategoriesMultiTenancy: safeArray(user.approverCategoriesMultiTenancy),
            approverCategoriesSTEnabled: safeArray(user.approverCategoriesSingleTenancy).length > 0,
            approverCategoriesMTEnabled: safeArray(user.approverCategoriesMultiTenancy).length > 0,
            linodeEnabled: !!user.linodeEnabled,
            preReleaseAccessEnabled: !!user.preReleaseAccessEnabled,
            autoProvisionEnabled: hasAutoProvision, // Main toggle based on any tenancy type enabled
            autoProvisionSingleTenancyEnabled: !!user.autoProvisionSingleTenancyEnabled,
            autoProvisionSingleTenancyCategories: safeArray(user.autoProvisionSingleTenancyCategories),
            autoProvisionMultiTenancyEnabled: !!user.autoProvisionMultiTenancyEnabled,
            autoProvisionMultiTenancyCategories: safeArray(user.autoProvisionMultiTenancyCategories)
        };

        setFormData(formDataToSet);
        setEditModalOpen(true);
    };

    const handleUpdateUser = async () => {
        try {
            setError(null);
            setSuccess(null);
            
            // Filter out SUB_APPROVER from roles before updating
            const filteredRoles = formData.roles.filter(role => role !== 'SUB_APPROVER');
            
            // If APPROVER role is removed, clear approver categories
            if (!filteredRoles.includes('APPROVER')) {
                formData.approverCategoriesSTEnabled = false;
                formData.approverCategoriesMTEnabled = false;
                formData.approverCategoriesSingleTenancy = [];
                formData.approverCategoriesMultiTenancy = [];
            }
            
            // Validate that APPROVER role has at least one approver category enabled (ST or MT)
            if (filteredRoles.includes('APPROVER')) {
                const stEnabled = !!formData.approverCategoriesSTEnabled;
                const mtEnabled = !!formData.approverCategoriesMTEnabled;
                const stSelected = Array.isArray(formData.approverCategoriesSingleTenancy) && formData.approverCategoriesSingleTenancy.length > 0;
                const mtSelected = Array.isArray(formData.approverCategoriesMultiTenancy) && formData.approverCategoriesMultiTenancy.length > 0;

                // At least one tenancy must be enabled
                if (!stEnabled && !mtEnabled) {
                    setError('At least one approver category (Single Tenancy or Multi Tenancy) must be enabled for an approver. Approvers can only see requests matching their assigned categories.');
                    return;
                }

                // If ST is enabled, categories must be selected
                if (stEnabled && !stSelected) {
                    setError('At least one Single Tenancy category must be selected when Single Tenancy approver categories are enabled.');
                    return;
                }

                // If MT is enabled, categories must be selected
                if (mtEnabled && !mtSelected) {
                    setError('At least one Multi Tenancy category must be selected when Multi Tenancy approver categories are enabled.');
                    return;
                }
            }

            // Validate that ST_FORM_FILLER has ST categories selected
            if (filteredRoles.includes('ST_FORM_FILLER')) {
                const hasSTCategories = Array.isArray(formData.categoriesSingleTenancy) && formData.categoriesSingleTenancy.length > 0;
                if (!hasSTCategories) {
                    setError('Single Tenancy categories are required when the ST_FORM_FILLER role is enabled. Please select at least one Single Tenancy category.');
                    return;
                }
            }

            // Validate that MT_FORM_FILLER has MT categories selected
            if (filteredRoles.includes('MT_FORM_FILLER')) {
                const hasMTCategories = Array.isArray(formData.categoriesMultiTenancy) && formData.categoriesMultiTenancy.length > 0;
                if (!hasMTCategories) {
                    setError('Multi Tenancy categories are required when the MT_FORM_FILLER role is enabled. Please select at least one Multi Tenancy category.');
                    return;
                }
            }
            
            // Ensure auto provisioning categories are cleared when disabled
            const autoProvisionSingleTenancyEnabled = !!formData.autoProvisionSingleTenancyEnabled;
            const autoProvisionMultiTenancyEnabled = !!formData.autoProvisionMultiTenancyEnabled;
            
            // Validate auto provisioning categories when enabled
            const autoProvisionSTSelected = Array.isArray(formData.autoProvisionSingleTenancyCategories) && formData.autoProvisionSingleTenancyCategories.length > 0;
            const autoProvisionMTSelected = Array.isArray(formData.autoProvisionMultiTenancyCategories) && formData.autoProvisionMultiTenancyCategories.length > 0;

            // If auto provisioning ST is enabled, categories must be selected
            if (autoProvisionSingleTenancyEnabled && !autoProvisionSTSelected) {
                setError('At least one Single Tenancy category must be selected when auto provisioning for Single Tenancy is enabled.');
                return;
            }

            // If auto provisioning MT is enabled, categories must be selected
            if (autoProvisionMultiTenancyEnabled && !autoProvisionMTSelected) {
                setError('At least one Multi Tenancy category must be selected when auto provisioning for Multi Tenancy is enabled.');
                return;
            }
            const autoProvisionSingleTenancyCategories = autoProvisionSingleTenancyEnabled 
                ? (formData.autoProvisionSingleTenancyCategories || [])
                : [];
            const autoProvisionMultiTenancyCategories = autoProvisionMultiTenancyEnabled
                ? (formData.autoProvisionMultiTenancyCategories || [])
                : [];
            
            // Use categories from form data, no auto-population for DEVOPS
            const categoriesST = Array.isArray(formData.categoriesSingleTenancy) ? formData.categoriesSingleTenancy : [];
            const categoriesMT = Array.isArray(formData.categoriesMultiTenancy) ? formData.categoriesMultiTenancy : [];
            // Only assign approver categories if the toggle is enabled
            const approverCategoriesST = formData.approverCategoriesSTEnabled
                ? (Array.isArray(formData.approverCategoriesSingleTenancy) ? formData.approverCategoriesSingleTenancy : [])
                : [];
            const approverCategoriesMT = formData.approverCategoriesMTEnabled
                ? (Array.isArray(formData.approverCategoriesMultiTenancy) ? formData.approverCategoriesMultiTenancy : [])
                : [];

            // Ensure all arrays are properly formatted (even if empty)
            const ensureArray = (arr) => {
                if (!arr) return [];
                if (Array.isArray(arr)) return arr.filter(c => c && c.trim());
                return [];
            };

            console.log('Updating user with data:', {
                email: editingEmail,
                roles: filteredRoles,
                isDEVOPS: filteredRoles.includes('DEVOPS'),
                categoriesSingleTenancy: categoriesST,
                categoriesMultiTenancy: categoriesMT,
                approverCategoriesSingleTenancy: approverCategoriesST,
                approverCategoriesMultiTenancy: approverCategoriesMT,
                singleTenancyEnabled: autoProvisionSingleTenancyEnabled,
                singleTenancyCategories: autoProvisionSingleTenancyCategories,
                multiTenancyEnabled: autoProvisionMultiTenancyEnabled,
                multiTenancyCategories: autoProvisionMultiTenancyCategories,
                formData_categoriesSingleTenancy: formData.categoriesSingleTenancy,
                formData_categoriesMultiTenancy: formData.categoriesMultiTenancy
            });
            
            const response = await api.admin.updateUser(
                editingEmail,
                filteredRoles,
                ensureArray(categoriesST),
                ensureArray(categoriesMT),
                ensureArray(approverCategoriesST),
                ensureArray(approverCategoriesMT),
                autoProvisionSingleTenancyEnabled || false,
                ensureArray(autoProvisionSingleTenancyCategories),
                autoProvisionMultiTenancyEnabled || false,
                ensureArray(autoProvisionMultiTenancyCategories),
                !!formData.linodeEnabled,
                !!formData.preReleaseAccessEnabled
            );
            if (response.data && response.data.success) {
                setSuccess('User updated successfully');
                setEditModalOpen(false);
                setFormData(createEmptyFormData());
                setEditingEmail('');
                setError(null);
                
                // Dispatch event to notify other components (like MainApp) that a user was updated
                window.dispatchEvent(new CustomEvent('userUpdated', {
                    detail: { email: editingEmail }
                }));
                
                // Force refresh the user list to show the updated user
                // Add small delay to ensure backend has processed the update
                await new Promise(resolve => setTimeout(resolve, 300));
                setLoading(true);
                try {
                    await fetchUsers();
                } catch (fetchError) {
                    console.error('Error refreshing user list:', fetchError);
                    // Don't show error for refresh failure, just log it
                } finally {
                    setLoading(false);
                }
                setTimeout(() => setSuccess(null), 3000);
            } else {
                throw new Error(response.data?.message || 'Failed to update user');
            }
        } catch (err) {
            console.error('Error updating user:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update user';
            setError(errorMessage);
            // Keep modal open so user can see the error
        }
    };

    const handleDeleteClick = (email) => {
        setDeletingEmail(email);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setError(null);
            setSuccess(null);
            const response = await api.admin.deleteUser(deletingEmail);
            if (response.data && response.data.success) {
                setSuccess('User deleted successfully');
                setDeleteConfirmOpen(false);
                setDeletingEmail('');
                // Refresh the user list to reflect the deletion
                await fetchUsers();
                setTimeout(() => setSuccess(null), 3000);
            } else {
                throw new Error(response.data?.message || 'Failed to delete user');
            }
        } catch (err) {
            console.error('Error deleting user:', err);
            setError(err.response?.data?.message || err.message || 'Failed to delete user');
            setDeleteConfirmOpen(false);
        }
    };

    const toggleRole = (role) => {
        setFormData(prev => {
            const isAdding = !prev.roles.includes(role);
            const newRoles = isAdding
                ? [...prev.roles, role]
                : prev.roles.filter(r => r !== role);
            
            // Just update roles, no auto-enabling logic
            return {
                ...prev,
                roles: newRoles
            };
        });
    };

    const toggleSingleTenancyAutoProvisionCategory = (category) => {
        setFormData(prev => ({
            ...prev,
            autoProvisionSingleTenancyCategories: prev.autoProvisionSingleTenancyCategories.includes(category)
                ? prev.autoProvisionSingleTenancyCategories.filter(c => c !== category)
                : [...prev.autoProvisionSingleTenancyCategories, category]
        }));
    };

    const toggleMultiTenancyAutoProvisionCategory = (category) => {
        setFormData(prev => ({
            ...prev,
            autoProvisionMultiTenancyCategories: prev.autoProvisionMultiTenancyCategories.includes(category)
                ? prev.autoProvisionMultiTenancyCategories.filter(c => c !== category)
                : [...prev.autoProvisionMultiTenancyCategories, category]
        }));
    };

    // ST/MT Category toggle functions
    const toggleCategorySingleTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            categoriesSingleTenancy: prev.categoriesSingleTenancy.includes(category)
                ? prev.categoriesSingleTenancy.filter(c => c !== category)
                : [...prev.categoriesSingleTenancy, category]
        }));
    };

    const toggleCategoryMultiTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            categoriesMultiTenancy: prev.categoriesMultiTenancy.includes(category)
                ? prev.categoriesMultiTenancy.filter(c => c !== category)
                : [...prev.categoriesMultiTenancy, category]
        }));
    };

    const toggleApproverCategorySingleTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            approverCategoriesSingleTenancy: prev.approverCategoriesSingleTenancy.includes(category)
                ? prev.approverCategoriesSingleTenancy.filter(c => c !== category)
                : [...prev.approverCategoriesSingleTenancy, category]
        }));
    };

    const toggleApproverCategoryMultiTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            approverCategoriesMultiTenancy: prev.approverCategoriesMultiTenancy.includes(category)
                ? prev.approverCategoriesMultiTenancy.filter(c => c !== category)
                : [...prev.approverCategoriesMultiTenancy, category]
        }));
    };

    const removeCategorySingleTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            categoriesSingleTenancy: prev.categoriesSingleTenancy.filter(c => c !== category)
        }));
    };

    const removeCategoryMultiTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            categoriesMultiTenancy: prev.categoriesMultiTenancy.filter(c => c !== category)
        }));
    };

    const removeApproverCategorySingleTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            approverCategoriesSingleTenancy: prev.approverCategoriesSingleTenancy.filter(c => c !== category)
        }));
    };

    const removeApproverCategoryMultiTenancy = (category) => {
        setFormData(prev => ({
            ...prev,
            approverCategoriesMultiTenancy: prev.approverCategoriesMultiTenancy.filter(c => c !== category)
        }));
    };

    const handleSelectAllRoles = () => {
        const allSelected = availableRoles.every(role => formData.roles.includes(role));
        if (allSelected) {
            setFormData(prev => ({ ...prev, roles: [] }));
        } else {
            setFormData(prev => ({ ...prev, roles: [...availableRoles] }));
        }
    };

    // Reset to first page when search changes
    useEffect(() => {
        setActivePage(1);
    }, [searchQuery, setActivePage]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setActivePage(1);
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setActivePage(1);
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setSelectedRoleFilter(null);
        setActivePage(1);
    };

    const handleRoleFilterClick = (role) => {
        if (selectedRoleFilter === role) {
            setSelectedRoleFilter(null); // Deselect if already selected
        } else {
            setSelectedRoleFilter(role);
        }
        setActivePage(1);
    };

    // Sorting state for table
    const [sortColumn, setSortColumn] = useState('email');
    const [sortDirection, setSortDirection] = useState('ascending'); // 'ascending' | 'descending'

    const handleSort = (column) => {
        if (sortColumn === column) {
            // Toggle direction
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        } else {
            setSortColumn(column);
            setSortDirection('ascending');
        }
    };

    // Filter users based on search query and role filter, then apply sorting
    const filteredUsers = useMemo(() => {
        let filtered = allUsers;
        
        // Apply role filter
        if (selectedRoleFilter) {
            filtered = filtered.filter(user => 
                user.roles && user.roles.includes(selectedRoleFilter)
            );
        }
        
        // Apply search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(user => {
                if (user.email.toLowerCase().includes(query)) return true;
                if (user.roles && user.roles.some(role => role.toLowerCase().includes(query))) return true;
                // Search in all category fields
                if (user.categories && user.categories.some(cat => cat.toLowerCase().includes(query))) return true;
                if (user.categoriesSingleTenancy && user.categoriesSingleTenancy.some(cat => cat.toLowerCase().includes(query))) return true;
                if (user.categoriesMultiTenancy && user.categoriesMultiTenancy.some(cat => cat.toLowerCase().includes(query))) return true;
                return false;
            });
        }
        return filtered;
    }, [allUsers, searchQuery, selectedRoleFilter]);

    const sortedUsers = useMemo(() => {
        const users = [...filteredUsers];

        const directionFactor = sortDirection === 'descending' ? -1 : 1;

        const getFieldValue = (user, column) => {
            switch (column) {
                case 'email':
                    return user.email?.toLowerCase() || '';
                case 'roles':
                    return (user.roles || []).join(', ').toLowerCase();
                case 'allowedCategories':
                    return [
                        ...(user.categoriesSingleTenancy || []),
                        ...(user.categoriesMultiTenancy || [])
                    ].join(', ').toLowerCase();
                case 'approverCategories':
                    return [
                        ...(user.approverCategoriesSingleTenancy || []),
                        ...(user.approverCategoriesMultiTenancy || [])
                    ].join(', ').toLowerCase();
                case 'autoProvision':
                    return user.autoProvisionEnabled ? 1 : 0;
                default:
                    return '';
            }
        };

        users.sort((a, b) => {
            const aVal = getFieldValue(a, sortColumn);
            const bVal = getFieldValue(b, sortColumn);

            if (aVal < bVal) return -1 * directionFactor;
            if (aVal > bVal) return 1 * directionFactor;
            // Fallback to email for stable-ish sort
            const aEmail = (a.email || '').toLowerCase();
            const bEmail = (b.email || '').toLowerCase();
            if (aEmail < bEmail) return -1;
            if (aEmail > bEmail) return 1;
            return 0;
        });

        return users;
    }, [filteredUsers, sortColumn, sortDirection]);

    // Calculate statistics (use allUsers for accurate totals)
    const totalUsers = allUsers.length;
    const usersByRole = allUsers.reduce((acc, user) => {
        user.roles
            .filter(role => role !== 'SUB_APPROVER') // Exclude deprecated SUB_APPROVER from statistics
            .forEach(role => {
            acc[role] = (acc[role] || 0) + 1;
        });
        return acc;
    }, {});
    const totalCategories = availableCategories.length;

    // Pagination
    const totalPages = Math.max(1, Math.ceil(sortedUsers.length / Math.max(1, rowsPerPage)));
    const pageStart = (activePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const visibleUsers = sortedUsers.slice(pageStart, pageEnd);

    const getRoleColor = (role) => {
        switch (role) {
            case 'ST_FORM_FILLER':
                return '#35B7F4'; // Secondary
            case 'MT_FORM_FILLER':
                return '#4F46E5'; // Indigo
            case 'MT_DECOMMISSION_REQUESTER':
                return '#0EA5E9'; // Sky
            case 'MT_DECOMMISSION_APPROVER':
                return '#14B8A6'; // Teal
            case 'DEVOPS':
                return '#475569'; // Slate/Dark Gray
            case 'APPROVER':
                return '#6F9911'; // Green
            case 'LICENSE_GENERATOR':
                return '#F97316'; // Orange
            case 'CAPACITY_READER':
                return '#9CA3AF'; // Light gray
            default:
                return '#6B7280'; // Default gray
        }
    };

    if (loading && allUsers.length === 0) {
        return (
            <div
                className="page-fade-in"
                style={{
                    padding: '2rem',
                    textAlign: 'center',
                    backgroundColor: '#f3f4f6',
                    minHeight: '60vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Loader active inline='centered' size='large'>
                    Loading users...
                </Loader>
            </div>
        );
    }

    return (
        <div
            className="page-fade-in"
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 200px)',
                minHeight: '600px',
                position: 'relative',
                backgroundColor: '#f3f4f6',
                padding: '1rem 0'
            }}
        >
            {/* Fixed Header */}
            <div
                style={{
                    flexShrink: 0,
                    backgroundColor: '#ffffff',
                    zIndex: 100,
                    padding: '1rem 1.25rem',
                    paddingBottom: '12px',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                    boxShadow: '0 6px 18px rgba(15,23,42,0.06)',
                    borderRadius: '12px',
                    margin: '0 1rem 0.75rem 1rem'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div style={{ flex: 1 }}>
                        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', color: '#062D4D' }}>
                            <Icon name='users' color='blue' />
                            <span style={{ fontWeight: 700, letterSpacing: '0.02em' }}>User Management</span>
                        </h3>
                        <div style={{ fontSize: '0.9em', color: '#4b5563', fontStyle: 'italic' }}>
                            Manage user roles and category access
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative' }}>
                            <Input
                                icon={searchQuery || selectedRoleFilter ? { name: 'close', link: true, onClick: handleClearSearch } : 'search'}
                                iconPosition='left'
                                placeholder={selectedRoleFilter ? `Filtered by: ${selectedRoleFilter}` : 'Search...'}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onKeyPress={handleSearchKeyPress}
                                style={{ minWidth: '220px', borderRadius: '999px', borderColor: '#e5e7eb' }}
                            />
                            {selectedRoleFilter && (
                                <Label 
                                    style={{ 
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '8px',
                                        backgroundColor: getRoleColor(selectedRoleFilter),
                                        color: '#FFFFFF',
                                        borderColor: getRoleColor(selectedRoleFilter),
                                        fontSize: '0.75em',
                                        padding: '2px 6px',
                                        zIndex: 10
                                    }}
                                >
                                    {selectedRoleFilter}
                                </Label>
                            )}
                        </div>
                        <Button
                            primary
                            icon
                            labelPosition='left'
                            onClick={() => {
                                setFormData(createEmptyFormData());
                                setAddModalOpen(true);
                            }}
                            style={{
                                borderRadius: '6px',
                                backgroundColor: '#062D4D',
                                border: 'none'
                            }}
                        >
                            <Icon name='plus' />
                            Add User
                        </Button>
                    </div>
                </div>

                {/* Statistics Segment */}
                <div style={{ 
                    display: 'flex', 
                    gap: '20px', 
                    flexWrap: 'wrap', 
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e1e8ed'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong style={{ color: '#666', fontWeight: 600 }}>Total Categories:</strong>
                        <Label 
                            size='large'
                            style={{
                                backgroundColor: '#35B7F4',
                                color: '#FFFFFF',
                                borderColor: '#35B7F4',
                                fontWeight: 600
                            }}
                        >
                            {totalCategories}
                        </Label>
                    </div>
                    {Object.keys(usersByRole).length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <strong style={{ color: '#666', fontWeight: 600 }}>Users by Role:</strong>
                            {Object.entries(usersByRole).map(([role, count]) => (
                                <Label 
                                    key={role}
                                    onClick={() => handleRoleFilterClick(role)}
                                    style={{ 
                                        marginLeft: '2px',
                                        backgroundColor: selectedRoleFilter === role ? getRoleColor(role) : getRoleColor(role),
                                        color: '#FFFFFF',
                                        borderColor: getRoleColor(role),
                                        cursor: 'pointer',
                                        opacity: selectedRoleFilter === role ? 1 : (selectedRoleFilter ? 0.6 : 1),
                                        boxShadow: selectedRoleFilter === role ? '0 0 0 2px rgba(0,0,0,0.2)' : 'none',
                                        transition: 'all 0.2s ease'
                                    }}
                                    title={`Click to filter by ${role}`}
                                >
                                    {role}: {count}
                                </Label>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Messages */}
            {error && (
                <Message negative onDismiss={() => setError(null)} style={{ margin: '0 1rem 1rem 1rem' }}>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {success && (
                <Message success onDismiss={() => setSuccess(null)} style={{ margin: '0 1rem 1rem 1rem' }}>
                    <Message.Header>Success</Message.Header>
                    <p>{success}</p>
                </Message>
            )}

            {/* Scrollable Table Container */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'auto',
                minHeight: 0,
                position: 'relative',
                padding: '0 1rem'
            }}>
                {filteredUsers.length === 0 ? (
                    <Message info style={{ margin: '1rem 0' }}>
                        <Message.Header>No Users Found</Message.Header>
                        <p>{searchQuery ? 'No users match your search criteria.' : 'Click "Add User" to create the first user in the system.'}</p>
                    </Message>
                ) : (
                    <Table celled striped selectable sortable compact={tableDensity === 'compact'} size={tableDensity === 'compact' ? 'small' : undefined}>
                        <Table.Header style={{ position: 'sticky', top: 0, zIndex: 50, backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <Table.Row>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'email' ? sortDirection : null}
                                    onClick={() => handleSort('email')}
                                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                >
                                    Email Address
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'roles' ? sortDirection : null}
                                    onClick={() => handleSort('roles')}
                                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                >
                                    Roles
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'allowedCategories' ? sortDirection : null}
                                    onClick={() => handleSort('allowedCategories')}
                                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                >
                                    Allowed Categories
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'approverCategories' ? sortDirection : null}
                                    onClick={() => handleSort('approverCategories')}
                                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                >
                                    Approver Categories
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    sorted={sortColumn === 'autoProvision' ? sortDirection : null}
                                    onClick={() => handleSort('autoProvision')}
                                    style={{ backgroundColor: 'white', cursor: 'pointer' }}
                                >
                                    Auto Provision
                                </Table.HeaderCell>
                                <Table.HeaderCell style={{ backgroundColor: 'white', width: '200px' }}>Actions</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {visibleUsers.map((user) => (
                                <Table.Row key={user.email?.toLowerCase() || `user-${Math.random()}`}>
                                    <Table.Cell>
                                        <strong>{user.email}</strong>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {user.roles && user.roles.filter(role => role !== 'SUB_APPROVER').length > 0 ? (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {user.roles
                                                    .filter(role => role !== 'SUB_APPROVER') // Filter out deprecated SUB_APPROVER role
                                                    .map(role => (
                                                    <Label 
                                                        key={role}
                                                        style={{ 
                                                            margin: '2px',
                                                            backgroundColor: getRoleColor(role),
                                                            color: '#FFFFFF',
                                                            borderColor: getRoleColor(role)
                                                        }}
                                                    >
                                                        {role}
                                                    </Label>
                                                ))}
                                            </div>
                                        ) : (
                                            <span style={{ color: '#999', fontStyle: 'italic' }}>No roles assigned</span>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {/* Single Tenancy Categories */}
                                            {((user.categoriesSingleTenancy && user.categoriesSingleTenancy.length > 0) || 
                                             ((!user.categoriesSingleTenancy || user.categoriesSingleTenancy.length === 0) && user.categories && user.categories.length > 0)) && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <div style={{ fontSize: '0.75em', color: '#062D4D', marginBottom: '6px', fontWeight: 600 }}>
                                                        Single Tenancy (ST):
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                        {(user.categoriesSingleTenancy && user.categoriesSingleTenancy.length > 0 
                                                            ? user.categoriesSingleTenancy 
                                                            : user.categories || []).map(category => (
                                                            <Label 
                                                                key={`st-${category}`} 
                                                                color='blue' 
                                                                size='tiny'
                                                                style={{ margin: 0 }}
                                                            >
                                                                {category}
                                                            </Label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Multi Tenancy Categories */}
                                            {user.categoriesMultiTenancy && user.categoriesMultiTenancy.length > 0 && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <div style={{ fontSize: '0.75em', color: '#062D4D', marginBottom: '6px', fontWeight: 600 }}>
                                                        Multi Tenancy (MT):
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                        {user.categoriesMultiTenancy.map(category => (
                                                            <Label 
                                                                key={`mt-${category}`} 
                                                                color='purple' 
                                                                size='tiny'
                                                                style={{ margin: 0 }}
                                                            >
                                                                {getMTCategoryDisplayName(category)}
                                                            </Label>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Show message if no categories at all */}
                                            {(!user.categoriesSingleTenancy || user.categoriesSingleTenancy.length === 0) &&
                                             (!user.categoriesMultiTenancy || user.categoriesMultiTenancy.length === 0) &&
                                             (!user.categories || user.categories.length === 0) && (
                                                <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9em' }}>No categories assigned</span>
                                            )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {/* Only show approver categories if user has APPROVER role */}
                                            {user.roles && user.roles.includes('APPROVER') ? (
                                                <>
                                                    {/* Single Tenancy Approver Categories */}
                                                    {user.approverCategoriesSingleTenancy && user.approverCategoriesSingleTenancy.length > 0 && (
                                                        <div style={{ marginBottom: '4px' }}>
                                                            <div style={{ fontSize: '0.75em', color: '#062D4D', marginBottom: '6px', fontWeight: 600 }}>
                                                                Single Tenancy (ST):
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {user.approverCategoriesSingleTenancy.map(category => (
                                                                    <Label 
                                                                        key={`ast-${category}`} 
                                                                        color='blue' 
                                                                        size='tiny'
                                                                        style={{ margin: 0 }}
                                                                    >
                                                                        {category}
                                                                    </Label>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Multi Tenancy Approver Categories */}
                                                    {user.approverCategoriesMultiTenancy && user.approverCategoriesMultiTenancy.length > 0 && (
                                                        <div style={{ marginBottom: '4px' }}>
                                                            <div style={{ fontSize: '0.75em', color: '#062D4D', marginBottom: '6px', fontWeight: 600 }}>
                                                                Multi Tenancy (MT):
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                {user.approverCategoriesMultiTenancy.map(category => (
                                                                    <Label 
                                                                        key={`amt-${category}`} 
                                                                        color='purple' 
                                                                        size='tiny'
                                                                        style={{ margin: 0 }}
                                                                    >
                                                                        {getMTCategoryDisplayName(category)}
                                                                    </Label>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Show message if no approver categories at all */}
                                                    {(!user.approverCategoriesSingleTenancy || user.approverCategoriesSingleTenancy.length === 0) &&
                                                     (!user.approverCategoriesMultiTenancy || user.approverCategoriesMultiTenancy.length === 0) && (
                                                        <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9em' }}>No approver categories assigned</span>
                                                    )}
                                                </>
                                            ) : (
                                                <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9em' }}>N/A (Not an approver)</span>
                                            )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {/* Single Tenancy */}
                                            {user.autoProvisionSingleTenancyEnabled ? (
                                            <div>
                                                    <Label color='green' size='tiny' style={{ marginBottom: '4px' }}>
                                                        Single Tenancy
                                                </Label>
                                                    {user.autoProvisionSingleTenancyCategories && user.autoProvisionSingleTenancyCategories.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                            {user.autoProvisionSingleTenancyCategories.map((cat) => (
                                                                <Label key={cat} basic color='green' size='mini' style={{ margin: 0 }}>
                                                                {cat}
                                                            </Label>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                            No categories
                                                    </div>
                                                )}
                                                </div>
                                            ) : null}
                                            {/* Multi Tenancy */}
                                            {user.autoProvisionMultiTenancyEnabled ? (
                                                <div>
                                                    <Label color='purple' size='tiny' style={{ marginBottom: '4px' }}>
                                                        Multi Tenancy
                                                    </Label>
                                                    {user.autoProvisionMultiTenancyCategories && user.autoProvisionMultiTenancyCategories.length > 0 ? (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                            {user.autoProvisionMultiTenancyCategories.map((cat) => (
                                                                <Label key={cat} basic color='purple' size='mini' style={{ margin: 0 }}>
                                                                    {cat}
                                                                </Label>
                                                            ))}
                                            </div>
                                        ) : (
                                                        <div style={{ fontSize: '0.8em', color: '#666' }}>
                                                            No categories
                                                        </div>
                                                    )}
                                                </div>
                                            ) : null}
                                            {!user.autoProvisionSingleTenancyEnabled && !user.autoProvisionMultiTenancyEnabled && (
                                            <span style={{ color: '#999', fontStyle: 'italic' }}>Disabled</span>
                                        )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <Button
                                                size='small'
                                                primary
                                                icon
                                                onClick={() => handleEditUser(user)}
                                            >
                                                <Icon name='edit' />
                                            </Button>
                                            <Button
                                                size='small'
                                                negative
                                                icon
                                                onClick={() => handleDeleteClick(user.email)}
                                            >
                                                <Icon name='trash' />
                                            </Button>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                )}
            </div>

            {/* Fixed Pagination */}
            {filteredUsers.length > 0 && (
                <PaginationFooter
                    activePage={activePage}
                    totalPages={totalPages}
                    totalItems={totalUsers}
                    filteredItems={sortedUsers}
                    hasActiveFilters={!!searchQuery || !!selectedRoleFilter}
                    onPageChange={setActivePage}
                    pageStart={pageStart}
                    pageEnd={pageEnd}
                />
            )}

            {/* Add User Modal */}
            <Modal
                open={addModalOpen}
                onClose={() => {
                    setAddModalOpen(false);
                    setFormData(createEmptyFormData());
                    setError(null);
                    resetEmailValidation();
                }}
                size='small'
                style={{ borderRadius: '8px', zIndex: 1001 }}
            >
                <Modal.Header style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Icon name='user plus' />
                    Add New User
                </Modal.Header>
                <Modal.Content style={{ padding: '20px', position: 'relative', maxHeight: '80vh', overflowY: 'auto' }}>
                    {error && (
                        <div 
                            ref={errorRef}
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10000,
                                backgroundColor: '#fff',
                                paddingBottom: '16px',
                                marginBottom: '16px'
                            }}
                        >
                            <Message 
                                negative 
                                style={{ 
                                    margin: 0,
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                }}
                                onDismiss={() => setError(null)}
                            >
                                <Message.Header>Error</Message.Header>
                                <p>{error}</p>
                            </Message>
                        </div>
                    )}
                    <Form>
                        <Form.Field required>
                            <label style={{ fontWeight: 600, marginBottom: '8px' }}>Email (@crashplan.com)</label>
                            <Input
                                placeholder='user@crashplan.com'
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                style={{ borderRadius: '6px' }}
                            />
                            {emailExists && (
                                <div style={{ 
                                    marginTop: '6px', 
                                    color: '#CE1B4E', 
                                    fontSize: '0.9em',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: 'transparent'
                                }}>
                                    <Icon 
                                        name='warning' 
                                        color='red' 
                                        style={{ 
                                            flexShrink: 0,
                                            verticalAlign: 'middle',
                                            backgroundColor: 'transparent',
                                            background: 'none'
                                        }} 
                                    />
                                    <strong style={{ lineHeight: '1.5', backgroundColor: 'transparent' }}>This user already exists in the system.</strong>
                                </div>
                            )}
                        </Form.Field>
                        <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                        {/* Cloud Provider Access */}
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '8px' }}>Cloud Provider Access</label>
                            <div style={{
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: '1px solid #e1e8ed',
                                background: formData.linodeEnabled ? '#e8f5e9' : '#f9fafb'
                            }}>
                                <Checkbox
                                    toggle
                                    label="Enable cloud provider: Linode"
                                    checked={!!formData.linodeEnabled}
                                    onChange={(e, data) => {
                                        setFormData(prev => ({ ...prev, linodeEnabled: !!data.checked }));
                                    }}
                                />
                            </div>
                        </Form.Field>
                        {/* Auto Provisioning Section */}
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '8px' }}>
                                Auto Provisioning
                            </label>
                            
                            {/* Main Auto Provisioning Toggle */}
                            <div style={{ 
                                marginBottom: '16px', 
                                padding: '16px', 
                                background: formData.autoProvisionEnabled ? '#e8f5e9' : '#f5f5f5', 
                                borderRadius: '8px', 
                                border: `2px solid ${formData.autoProvisionEnabled ? '#4caf50' : '#e1e8ed'}`,
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Icon 
                                            name={formData.autoProvisionEnabled ? 'check circle' : 'circle outline'} 
                                            color={formData.autoProvisionEnabled ? 'green' : 'grey'}
                                            size='large'
                                        />
                                        <strong style={{ fontSize: '1.2em', color: formData.autoProvisionEnabled ? '#2e7d32' : '#666' }}>
                                            Auto Provisioning
                                        </strong>
                                    </div>
                                    <Button
                                        toggle
                                        active={formData.autoProvisionEnabled}
                                        onClick={() => {
                                            const newValue = !formData.autoProvisionEnabled;
                                            setFormData(prev => ({
                                                ...prev,
                                                autoProvisionEnabled: newValue,
                                                // If disabling main toggle, disable and clear all sub-options
                                                autoProvisionSingleTenancyEnabled: newValue ? prev.autoProvisionSingleTenancyEnabled : false,
                                                autoProvisionMultiTenancyEnabled: newValue ? prev.autoProvisionMultiTenancyEnabled : false,
                                                autoProvisionSingleTenancyCategories: newValue ? prev.autoProvisionSingleTenancyCategories : [],
                                                autoProvisionMultiTenancyCategories: newValue ? prev.autoProvisionMultiTenancyCategories : []
                                            }));
                                        }}
                                        color={formData.autoProvisionEnabled ? 'green' : 'grey'}
                                        size='large'
                                        style={{ 
                                            minWidth: '100px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {formData.autoProvisionEnabled ? 'Enabled' : 'Disabled'}
                                    </Button>
                                </div>
                                <div style={{ 
                                    fontSize: '0.9em', 
                                    color: formData.autoProvisionEnabled ? '#2e7d32' : '#999',
                                    fontStyle: 'italic',
                                    marginTop: '8px'
                                }}>
                                    {formData.autoProvisionEnabled 
                                        ? '✓ Auto provisioning is active. Configure single and multi tenancy settings below.'
                                        : 'Enable auto provisioning to configure single and multi tenancy settings.'}
                                </div>
                            </div>

                            {formData.autoProvisionEnabled && (
                                <>
                                    <Message info size="small" style={{ marginBottom: '12px' }}>
                                        <Message.Header>Auto Provisioning Configuration</Message.Header>
                                    <p>
                                            Configure auto-provisioning separately for single tenancy and multi tenancy setups.
                                            Each tenancy type can have its own set of allowed categories.
                                    </p>
                                </Message>
                                    
                                    {/* Single Tenancy Auto Provisioning */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable auto provisioning for Single Tenancy"
                                                checked={formData.autoProvisionSingleTenancyEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        autoProvisionSingleTenancyEnabled: checked,
                                                        autoProvisionSingleTenancyCategories: checked ? prev.autoProvisionSingleTenancyCategories : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.autoProvisionSingleTenancyEnabled && (
                                            <div style={{ marginTop: '8px' }}>
                                                <div style={{ marginBottom: '12px' }}>
                                                    <Input
                                                        placeholder='Type category and press Enter to add'
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                                                const newCategory = e.target.value.trim();
                                                                if (!formData.autoProvisionSingleTenancyCategories.includes(newCategory)) {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        autoProvisionSingleTenancyCategories: [...prev.autoProvisionSingleTenancyCategories, newCategory]
                                                                    }));
                                                                    if (!availableCategories.includes(newCategory)) {
                                                                        setAvailableCategories(prev => [...prev, newCategory].sort());
                                                                    }
                                                                }
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                        style={{ borderRadius: '6px' }}
                                                    />
                                                </div>
                                                {availableCategories.length > 0 || formData.autoProvisionSingleTenancyCategories.length > 0 ? (
                                                    <>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                                Select Categories {formData.autoProvisionSingleTenancyEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                                            </label>
                                                            {(availableCategories.length > 0 || formData.autoProvisionSingleTenancyCategories.length > 0) && (
                                                                <Button
                                                                    size='tiny'
                                                                    onClick={() => {
                                                                        const allCats = [...new Set([...availableCategories, ...formData.autoProvisionSingleTenancyCategories])];
                                                                        const allSelected = allCats.every(cat => formData.autoProvisionSingleTenancyCategories.includes(cat));
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            autoProvisionSingleTenancyCategories: allSelected ? [] : allCats
                                                                        }));
                                                                    }}
                                                                    style={{ fontSize: '0.85em' }}
                                                                >
                                                                    {[...new Set([...availableCategories, ...formData.autoProvisionSingleTenancyCategories])].every(cat => formData.autoProvisionSingleTenancyCategories.includes(cat)) ? 'Deselect All' : 'Select All'}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                            {[...new Set([...availableCategories, ...formData.autoProvisionSingleTenancyCategories])].map(category => (
                                                            <Checkbox
                                                                key={category}
                                                                label={category}
                                                                checked={formData.autoProvisionSingleTenancyCategories.includes(category)}
                                                                onChange={() => toggleSingleTenancyAutoProvisionCategory(category)}
                                                            />
                                                        ))}
                                                    </div>
                                                    </>
                                                ) : (
                                                    <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                        Type a category name above and press Enter to add it.
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Multi Tenancy Auto Provisioning */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable auto provisioning for Multi Tenancy"
                                                checked={formData.autoProvisionMultiTenancyEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        autoProvisionMultiTenancyEnabled: checked,
                                                        autoProvisionMultiTenancyCategories: checked ? prev.autoProvisionMultiTenancyCategories : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.autoProvisionMultiTenancyEnabled && (
                                <div style={{ marginTop: '8px' }}>
                                                {validMultiTenancyCategories.length > 0 ? (
                                                    <>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                                Select Categories {formData.autoProvisionMultiTenancyEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                                            </label>
                                                            <Button
                                                                size='tiny'
                                                                onClick={() => {
                                                                    const allSelected = validMultiTenancyCategories.every(cat => formData.autoProvisionMultiTenancyCategories.includes(cat));
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        autoProvisionMultiTenancyCategories: allSelected ? [] : [...validMultiTenancyCategories]
                                                                    }));
                                                                }}
                                                                style={{ fontSize: '0.85em' }}
                                                            >
                                                                {validMultiTenancyCategories.every(cat => formData.autoProvisionMultiTenancyCategories.includes(cat)) ? 'Deselect All' : 'Select All'}
                                                            </Button>
                                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                            {validMultiTenancyCategories.map(category => (
                                                <Checkbox
                                                    key={category}
                                                                    label={getMTCategoryDisplayName(category)}
                                                                checked={formData.autoProvisionMultiTenancyCategories.includes(category)}
                                                                onChange={() => toggleMultiTenancyAutoProvisionCategory(category)}
                                                />
                                            ))}
                                        </div>
                                                    </>
                                    ) : (
                                        <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                        No valid multi-tenancy categories available.
                                        </span>
                                    )}
                                </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </Form.Field>
                        <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                        <Form.Field>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <label style={{ fontWeight: 600, margin: 0 }}>
                                    Roles <span style={{ color: '#CE1B4E' }}>*</span>
                                </label>
                                <Button
                                    size='tiny'
                                    onClick={handleSelectAllRoles}
                                    style={{ fontSize: '0.85em' }}
                                >
                                    {availableRoles.every(role => formData.roles.includes(role)) ? 'Deselect All' : 'Select All'}
                                </Button>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                                {availableRoles.map(role => (
                                    <Checkbox
                                        key={role}
                                        label={getRoleDisplayLabel(role)}
                                        checked={formData.roles.includes(role)}
                                        onChange={() => toggleRole(role)}
                                    />
                                ))}
                            </div>
                        </Form.Field>
                        <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                        {/* Approver Category Configuration - Only shown when APPROVER role is selected */}
                        {formData.roles.includes('APPROVER') && (
                            <>
                                <Form.Field>
                                    <label style={{ fontWeight: 600, marginBottom: '12px', color: '#062D4D', display: 'block' }}>
                                        Approver Categories <span style={{ color: '#CE1B4E' }}>*</span>
                                    </label>
                                    
                                    {/* Single Tenancy Approver Categories */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable Single Tenancy Approver Categories"
                                                checked={formData.approverCategoriesSTEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        approverCategoriesSTEnabled: checked,
                                                        approverCategoriesSingleTenancy: checked ? prev.approverCategoriesSingleTenancy : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.approverCategoriesSTEnabled && (
                                            <div style={{ marginTop: '12px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                                                <div style={{ marginBottom: '12px' }}>
                                                    <Input
                                                        placeholder='Type category and press Enter to add'
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                                                const newCategory = e.target.value.trim();
                                                                if (!formData.approverCategoriesSingleTenancy.includes(newCategory)) {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        approverCategoriesSingleTenancy: [...prev.approverCategoriesSingleTenancy, newCategory]
                                                                    }));
                                                                    if (!availableCategories.includes(newCategory)) {
                                                                        setAvailableCategories(prev => [...prev, newCategory].sort());
                                                                    }
                                                                }
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                        style={{ borderRadius: '6px' }}
                                                    />
                                                </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                        Single Tenancy Approver Categories {formData.approverCategoriesSTEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                        </label>
                                                    {(availableCategories.length > 0 || formData.approverCategoriesSingleTenancy.length > 0) && (
                                            <Button
                                                size='tiny'
                                                            onClick={() => {
                                                                const allCats = [...new Set([...availableCategories, ...formData.approverCategoriesSingleTenancy])];
                                                                const allSelected = allCats.every(cat => formData.approverCategoriesSingleTenancy.includes(cat));
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    approverCategoriesSingleTenancy: allSelected ? [] : allCats
                                                                }));
                                                            }}
                                                style={{ fontSize: '0.85em' }}
                                            >
                                                            {[...new Set([...availableCategories, ...formData.approverCategoriesSingleTenancy])].every(cat => formData.approverCategoriesSingleTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                            </Button>
                                        )}
                                    </div>
                                                {availableCategories.length > 0 || formData.approverCategoriesSingleTenancy.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                                        {[...new Set([...availableCategories, ...formData.approverCategoriesSingleTenancy])].map(category => (
                                                <Checkbox
                                                    key={category}
                                                    label={category}
                                                                checked={formData.approverCategoriesSingleTenancy.includes(category)}
                                                                onChange={() => toggleApproverCategorySingleTenancy(category)}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <Message info size="small" style={{ marginBottom: '12px' }}>
                                                        <p>Type a category name above and press Enter to add it.</p>
                                        </Message>
                                    )}
                                                {formData.approverCategoriesSingleTenancy.length > 0 && (
                                        <div style={{ marginTop: '12px' }}>
                                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {formData.approverCategoriesSingleTenancy.map(category => (
                                                                <Label key={category} color='blue' style={{ margin: 0 }}>
                                                        {category}
                                                        <Icon
                                                            name='delete'
                                                                        onClick={() => removeApproverCategorySingleTenancy(category)}
                                                            style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                        />
                                                    </Label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Multi Tenancy Approver Categories */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable Multi Tenancy Approver Categories"
                                                checked={formData.approverCategoriesMTEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        approverCategoriesMTEnabled: checked,
                                                        approverCategoriesMultiTenancy: checked ? prev.approverCategoriesMultiTenancy : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.approverCategoriesMTEnabled && (
                                            <div style={{ marginTop: '12px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                    <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                        Multi Tenancy Approver Categories {formData.approverCategoriesMTEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                                    </label>
                                                    {validMultiTenancyCategories.length > 0 && (
                                                        <Button
                                                            size='tiny'
                                                            onClick={() => {
                                                                const allSelected = validMultiTenancyCategories.every(cat => formData.approverCategoriesMultiTenancy.includes(cat));
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    approverCategoriesMultiTenancy: allSelected ? [] : [...validMultiTenancyCategories]
                                                                }));
                                                            }}
                                                            style={{ fontSize: '0.85em' }}
                                                        >
                                                            {validMultiTenancyCategories.every(cat => formData.approverCategoriesMultiTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                                        </Button>
                                                    )}
                                                </div>
                                                {validMultiTenancyCategories.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                                        {validMultiTenancyCategories.map(category => (
                                                            <Checkbox
                                                                key={category}
                                                                label={getMTCategoryDisplayName(category)}
                                                                checked={formData.approverCategoriesMultiTenancy.includes(category)}
                                                                onChange={() => toggleApproverCategoryMultiTenancy(category)}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <Message info size="small" style={{ marginBottom: '12px' }}>
                                                        <p>No valid multi-tenancy categories available.</p>
                                                    </Message>
                                                )}
                                                {formData.approverCategoriesMultiTenancy.length > 0 && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {formData.approverCategoriesMultiTenancy.map(category => (
                                                                <Label key={category} color='purple' style={{ margin: 0 }}>
                                                                    {getMTCategoryDisplayName(category)}
                                                                    <Icon
                                                                        name='delete'
                                                                        onClick={() => removeApproverCategoryMultiTenancy(category)}
                                                                        style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                                    />
                                                                </Label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {formData.roles.includes('APPROVER') && 
                                     !formData.approverCategoriesSTEnabled && 
                                     !formData.approverCategoriesMTEnabled && (
                                        <Message warning size="small" style={{ marginTop: '12px' }}>
                                            <Message.Header>Category Required</Message.Header>
                                            <p>At least one approver category (Single Tenancy or Multi Tenancy) must be enabled. The approver will only see requests matching their assigned categories.</p>
                                        </Message>
                                    )}
                                </Form.Field>
                                <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                            </>
                        )}
                        {/* General Categories - ST/MT Separated based on roles */}
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '12px', display: 'block' }}>
                                General Categories <span style={{ color: '#CE1B4E' }}>*</span>
                            </label>
                            
                            {/* Single Tenancy Categories - Only shown when ST_FORM_FILLER role is enabled */}
                            {formData.roles.includes('ST_FORM_FILLER') && (
                            <div style={{ marginBottom: '20px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                        Single Tenancy Categories <span style={{ color: '#CE1B4E' }}>*</span>
                                    </label>
                                    {(availableCategories.length > 0 || formData.categoriesSingleTenancy.length > 0) && (
                                        <Button
                                            size='tiny'
                                            onClick={() => {
                                                const allCats = [...new Set([...availableCategories, ...formData.categoriesSingleTenancy])];
                                                const allSelected = allCats.every(cat => formData.categoriesSingleTenancy.includes(cat));
                                                setFormData(prev => ({
                                                    ...prev,
                                                    categoriesSingleTenancy: allSelected ? [] : allCats
                                                }));
                                            }}
                                            style={{ fontSize: '0.85em' }}
                                        >
                                            {[...new Set([...availableCategories, ...formData.categoriesSingleTenancy])].every(cat => formData.categoriesSingleTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    )}
                                </div>
                                <div style={{ marginBottom: '12px' }}>
                                    <Input
                                        placeholder='Type category and press Enter'
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                                const newCategory = e.target.value.trim();
                                                if (!formData.categoriesSingleTenancy.includes(newCategory) && !availableCategories.includes(newCategory)) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        categoriesSingleTenancy: [...prev.categoriesSingleTenancy, newCategory]
                                                    }));
                                                    setAvailableCategories(prev => [...prev, newCategory].sort());
                                                }
                                                e.target.value = '';
                                            }
                                        }}
                                        style={{ borderRadius: '6px' }}
                                    />
                                </div>
                                {(availableCategories.length > 0 || formData.categoriesSingleTenancy.length > 0) && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                        {[...new Set([...availableCategories, ...formData.categoriesSingleTenancy])].map(category => (
                                            <Checkbox
                                                key={category}
                                                label={category}
                                                checked={formData.categoriesSingleTenancy.includes(category)}
                                                onChange={() => toggleCategorySingleTenancy(category)}
                                            />
                                        ))}
                                    </div>
                                )}
                                {formData.categoriesSingleTenancy.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {formData.categoriesSingleTenancy.map(category => (
                                                <Label key={category} color='blue' style={{ margin: 0 }}>
                                                    {category}
                                                    <Icon
                                                        name='delete'
                                                        onClick={() => removeCategorySingleTenancy(category)}
                                                        style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                    />
                                                </Label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Multi Tenancy Categories - Only shown when MT_FORM_FILLER role is enabled */}
                            {formData.roles.includes('MT_FORM_FILLER') && (
                            <div style={{ marginBottom: '20px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                        Multi Tenancy Categories <span style={{ color: '#CE1B4E' }}>*</span>
                                    </label>
                                    {validMultiTenancyCategories.length > 0 && (
                                        <Button
                                            size='tiny'
                                            onClick={() => {
                                                const allSelected = validMultiTenancyCategories.every(cat => formData.categoriesMultiTenancy.includes(cat));
                                                setFormData(prev => ({
                                                    ...prev,
                                                    categoriesMultiTenancy: allSelected ? [] : [...validMultiTenancyCategories]
                                                }));
                                            }}
                                            style={{ fontSize: '0.85em' }}
                                        >
                                            {validMultiTenancyCategories.every(cat => formData.categoriesMultiTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    )}
                                </div>
                                {validMultiTenancyCategories.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                        {validMultiTenancyCategories.map(category => (
                                            <Checkbox
                                                key={category}
                                                label={getMTCategoryDisplayName(category)}
                                                checked={formData.categoriesMultiTenancy.includes(category)}
                                                onChange={() => toggleCategoryMultiTenancy(category)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <Message info size="small" style={{ marginBottom: '12px' }}>
                                        <p>No valid multi-tenancy categories available.</p>
                                    </Message>
                                )}
                                {formData.categoriesMultiTenancy.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {formData.categoriesMultiTenancy.map(category => (
                                                <Label key={category} color='purple' style={{ margin: 0 }}>
                                                    {getMTCategoryDisplayName(category)}
                                                    <Icon
                                                        name='delete'
                                                        onClick={() => removeCategoryMultiTenancy(category)}
                                                        style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                    />
                                                </Label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                    </div>
                                )}
                            </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions style={{ borderTop: '1px solid rgba(0,0,0,0.08)', padding: '12px 20px' }}>
                    <Button onClick={() => {
                        setAddModalOpen(false);
                        setFormData(createEmptyFormData());
                        setError(null);
                        resetEmailValidation();
                    }}>
                        Cancel
                    </Button>
                    <Button 
                        primary 
                        onClick={handleAddUser}
                        disabled={emailExists}
                    >
                        <Icon name='check' />
                        Add User
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Edit User Modal */}
            <Modal
                open={editModalOpen}
                onClose={() => {
                    setEditModalOpen(false);
                    setFormData(createEmptyFormData());
                    setEditingEmail('');
                    setError(null);
                }}
                size='small'
                style={{ borderRadius: '8px' }}
            >
                <Modal.Header style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Icon name='edit' />
                    Edit User: {editingEmail}
                </Modal.Header>
                <Modal.Content style={{ padding: '20px', position: 'relative', maxHeight: '80vh', overflowY: 'auto' }}>
                    {error && (
                        <div 
                            ref={errorRef}
                            style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10000,
                                backgroundColor: '#fff',
                                paddingBottom: '16px',
                                marginBottom: '16px'
                            }}
                        >
                            <Message 
                                negative 
                                style={{ 
                                    margin: 0,
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                                }}
                                onDismiss={() => setError(null)}
                            >
                                <Message.Header>Error</Message.Header>
                                <p>{error}</p>
                            </Message>
                        </div>
                    )}
                    <Form>
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '8px' }}>Email</label>
                            <Input value={formData.email} disabled style={{ borderRadius: '6px' }} />
                        </Form.Field>
                        <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                        {/* Cloud Provider Access */}
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '8px' }}>Cloud Provider Access</label>
                            <div style={{
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: '1px solid #e1e8ed',
                                background: formData.linodeEnabled ? '#e8f5e9' : '#f9fafb'
                            }}>
                                <Checkbox
                                    toggle
                                    label="Enable cloud provider: Linode"
                                    checked={!!formData.linodeEnabled}
                                    onChange={(e, data) => {
                                        setFormData(prev => ({ ...prev, linodeEnabled: !!data.checked }));
                                    }}
                                />
                            </div>
                        </Form.Field>
                        {/* Allow Access During Maintenance */}
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '8px' }}>Allow Access During Maintenance</label>
                            <div style={{
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: '1px solid #e1e8ed',
                                background: formData.preReleaseAccessEnabled ? '#e8f5e9' : '#f9fafb'
                            }}>
                                <Checkbox
                                    toggle
                                    label="Allow portal access during maintenance"
                                    checked={!!formData.preReleaseAccessEnabled}
                                    onChange={(e, data) => {
                                        setFormData(prev => ({ ...prev, preReleaseAccessEnabled: !!data.checked }));
                                    }}
                                />
                            </div>
                        </Form.Field>
                        {/* Auto Provisioning Section (Edit) */}
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '8px' }}>
                                Auto Provisioning
                            </label>
                            
                            {/* Main Auto Provisioning Toggle */}
                            <div style={{ 
                                marginBottom: '16px', 
                                padding: '16px', 
                                background: formData.autoProvisionEnabled ? '#e8f5e9' : '#f5f5f5', 
                                borderRadius: '8px', 
                                border: `2px solid ${formData.autoProvisionEnabled ? '#4caf50' : '#e1e8ed'}`,
                                transition: 'all 0.3s ease'
                            }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Icon 
                                            name={formData.autoProvisionEnabled ? 'check circle' : 'circle outline'} 
                                            color={formData.autoProvisionEnabled ? 'green' : 'grey'}
                                            size='large'
                                        />
                                        <strong style={{ fontSize: '1.2em', color: formData.autoProvisionEnabled ? '#2e7d32' : '#666' }}>
                                            Auto Provisioning
                                        </strong>
                                    </div>
                                    <Button
                                        toggle
                                        active={formData.autoProvisionEnabled}
                                        onClick={() => {
                                            const newValue = !formData.autoProvisionEnabled;
                                            setFormData(prev => ({
                                                ...prev,
                                                autoProvisionEnabled: newValue,
                                                // If disabling main toggle, disable and clear all sub-options
                                                autoProvisionSingleTenancyEnabled: newValue ? prev.autoProvisionSingleTenancyEnabled : false,
                                                autoProvisionMultiTenancyEnabled: newValue ? prev.autoProvisionMultiTenancyEnabled : false,
                                                autoProvisionSingleTenancyCategories: newValue ? prev.autoProvisionSingleTenancyCategories : [],
                                                autoProvisionMultiTenancyCategories: newValue ? prev.autoProvisionMultiTenancyCategories : []
                                            }));
                                        }}
                                        color={formData.autoProvisionEnabled ? 'green' : 'grey'}
                                        size='large'
                                        style={{ 
                                            minWidth: '100px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {formData.autoProvisionEnabled ? 'Enabled' : 'Disabled'}
                                    </Button>
                                </div>
                                <div style={{ 
                                    fontSize: '0.9em', 
                                    color: formData.autoProvisionEnabled ? '#2e7d32' : '#999',
                                    fontStyle: 'italic',
                                    marginTop: '8px'
                                }}>
                                    {formData.autoProvisionEnabled 
                                        ? '✓ Auto provisioning is active. Configure single and multi tenancy settings below.'
                                        : 'Enable auto provisioning to configure single and multi tenancy settings.'}
                                </div>
                            </div>

                            {formData.autoProvisionEnabled && (
                                <>
                                    <Message info size="small" style={{ marginBottom: '12px' }}>
                                        <Message.Header>Auto Provisioning Configuration</Message.Header>
                                    <p>
                                            Configure auto-provisioning separately for single tenancy and multi tenancy setups.
                                            Each tenancy type can have its own set of allowed categories.
                                    </p>
                                </Message>
                                    
                                    {/* Single Tenancy Auto Provisioning */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable auto provisioning for Single Tenancy"
                                                checked={formData.autoProvisionSingleTenancyEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        autoProvisionSingleTenancyEnabled: checked,
                                                        autoProvisionSingleTenancyCategories: checked ? prev.autoProvisionSingleTenancyCategories : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.autoProvisionSingleTenancyEnabled && (
                                            <div style={{ marginTop: '8px' }}>
                                                <div style={{ marginBottom: '12px' }}>
                                                    <Input
                                                        placeholder='Type category and press Enter to add'
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                                                const newCategory = e.target.value.trim();
                                                                if (!formData.autoProvisionSingleTenancyCategories.includes(newCategory)) {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        autoProvisionSingleTenancyCategories: [...prev.autoProvisionSingleTenancyCategories, newCategory]
                                                                    }));
                                                                    if (!availableCategories.includes(newCategory)) {
                                                                        setAvailableCategories(prev => [...prev, newCategory].sort());
                                                                    }
                                                                }
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                        style={{ borderRadius: '6px' }}
                                                    />
                                                </div>
                                                {availableCategories.length > 0 || formData.autoProvisionSingleTenancyCategories.length > 0 ? (
                                                    <>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                                Select Categories {formData.autoProvisionSingleTenancyEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                                            </label>
                                                            {(availableCategories.length > 0 || formData.autoProvisionSingleTenancyCategories.length > 0) && (
                                                                <Button
                                                                    size='tiny'
                                                                    onClick={() => {
                                                                        const allCats = [...new Set([...availableCategories, ...formData.autoProvisionSingleTenancyCategories])];
                                                                        const allSelected = allCats.every(cat => formData.autoProvisionSingleTenancyCategories.includes(cat));
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            autoProvisionSingleTenancyCategories: allSelected ? [] : allCats
                                                                        }));
                                                                    }}
                                                                    style={{ fontSize: '0.85em' }}
                                                                >
                                                                    {[...new Set([...availableCategories, ...formData.autoProvisionSingleTenancyCategories])].every(cat => formData.autoProvisionSingleTenancyCategories.includes(cat)) ? 'Deselect All' : 'Select All'}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                            {[...new Set([...availableCategories, ...formData.autoProvisionSingleTenancyCategories])].map(category => (
                                                            <Checkbox
                                                                key={category}
                                                                label={category}
                                                                checked={formData.autoProvisionSingleTenancyCategories.includes(category)}
                                                                onChange={() => toggleSingleTenancyAutoProvisionCategory(category)}
                                                            />
                                                        ))}
                                                    </div>
                                                    </>
                                                ) : (
                                                    <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                        Type a category name above and press Enter to add it.
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Multi Tenancy Auto Provisioning */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable auto provisioning for Multi Tenancy"
                                                checked={formData.autoProvisionMultiTenancyEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        autoProvisionMultiTenancyEnabled: checked,
                                                        autoProvisionMultiTenancyCategories: checked ? prev.autoProvisionMultiTenancyCategories : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.autoProvisionMultiTenancyEnabled && (
                                <div style={{ marginTop: '8px' }}>
                                                {validMultiTenancyCategories.length > 0 ? (
                                                    <>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                                Select Categories {formData.autoProvisionMultiTenancyEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                                            </label>
                                                            <Button
                                                                size='tiny'
                                                                onClick={() => {
                                                                    const allSelected = validMultiTenancyCategories.every(cat => formData.autoProvisionMultiTenancyCategories.includes(cat));
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        autoProvisionMultiTenancyCategories: allSelected ? [] : [...validMultiTenancyCategories]
                                                                    }));
                                                                }}
                                                                style={{ fontSize: '0.85em' }}
                                                            >
                                                                {validMultiTenancyCategories.every(cat => formData.autoProvisionMultiTenancyCategories.includes(cat)) ? 'Deselect All' : 'Select All'}
                                                            </Button>
                                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                            {validMultiTenancyCategories.map(category => (
                                                <Checkbox
                                                    key={category}
                                                                    label={getMTCategoryDisplayName(category)}
                                                                checked={formData.autoProvisionMultiTenancyCategories.includes(category)}
                                                                onChange={() => toggleMultiTenancyAutoProvisionCategory(category)}
                                                />
                                            ))}
                                        </div>
                                                    </>
                                    ) : (
                                        <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                        No valid multi-tenancy categories available.
                                        </span>
                                    )}
                                </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </Form.Field>
                        <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                        <Form.Field>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <label style={{ fontWeight: 600, margin: 0 }}>
                                    Roles <span style={{ color: '#CE1B4E' }}>*</span>
                                </label>
                                <Button
                                    size='tiny'
                                    onClick={handleSelectAllRoles}
                                    style={{ fontSize: '0.85em' }}
                                >
                                    {availableRoles.every(role => formData.roles.includes(role)) ? 'Deselect All' : 'Select All'}
                                </Button>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', background: '#f9fafb', borderRadius: '6px' }}>
                                {availableRoles.map(role => (
                                    <Checkbox
                                        key={role}
                                        label={getRoleDisplayLabel(role)}
                                        checked={formData.roles.includes(role)}
                                        onChange={() => toggleRole(role)}
                                    />
                                ))}
                            </div>
                        </Form.Field>
                        <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                        {/* Approver Category Configuration - Only shown when APPROVER role is selected */}
                        {formData.roles.includes('APPROVER') && (
                            <>
                                <Form.Field>
                                    <label style={{ fontWeight: 600, marginBottom: '12px', color: '#062D4D', display: 'block' }}>
                                        Approver Categories <span style={{ color: '#CE1B4E' }}>*</span>
                                    </label>
                                    
                                    {/* Single Tenancy Approver Categories */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable Single Tenancy Approver Categories"
                                                checked={formData.approverCategoriesSTEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        approverCategoriesSTEnabled: checked,
                                                        approverCategoriesSingleTenancy: checked ? prev.approverCategoriesSingleTenancy : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.approverCategoriesSTEnabled && (
                                            <div style={{ marginTop: '12px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                                                <div style={{ marginBottom: '12px' }}>
                                                    <Input
                                                        placeholder='Type category and press Enter to add'
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                                                const newCategory = e.target.value.trim();
                                                                if (!formData.approverCategoriesSingleTenancy.includes(newCategory)) {
                                                                    setFormData(prev => ({
                                                                        ...prev,
                                                                        approverCategoriesSingleTenancy: [...prev.approverCategoriesSingleTenancy, newCategory]
                                                                    }));
                                                                    if (!availableCategories.includes(newCategory)) {
                                                                        setAvailableCategories(prev => [...prev, newCategory].sort());
                                                                    }
                                                                }
                                                                e.target.value = '';
                                                            }
                                                        }}
                                                        style={{ borderRadius: '6px' }}
                                                    />
                                                </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                        <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                        Single Tenancy Approver Categories {formData.approverCategoriesSTEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                        </label>
                                                    {(availableCategories.length > 0 || formData.approverCategoriesSingleTenancy.length > 0) && (
                                            <Button
                                                size='tiny'
                                                            onClick={() => {
                                                                const allCats = [...new Set([...availableCategories, ...formData.approverCategoriesSingleTenancy])];
                                                                const allSelected = allCats.every(cat => formData.approverCategoriesSingleTenancy.includes(cat));
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    approverCategoriesSingleTenancy: allSelected ? [] : allCats
                                                                }));
                                                            }}
                                                style={{ fontSize: '0.85em' }}
                                            >
                                                            {[...new Set([...availableCategories, ...formData.approverCategoriesSingleTenancy])].every(cat => formData.approverCategoriesSingleTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                            </Button>
                                        )}
                                    </div>
                                                {availableCategories.length > 0 || formData.approverCategoriesSingleTenancy.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                                        {[...new Set([...availableCategories, ...formData.approverCategoriesSingleTenancy])].map(category => (
                                                <Checkbox
                                                    key={category}
                                                    label={category}
                                                                checked={formData.approverCategoriesSingleTenancy.includes(category)}
                                                                onChange={() => toggleApproverCategorySingleTenancy(category)}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <Message info size="small" style={{ marginBottom: '12px' }}>
                                                        <p>Type a category name above and press Enter to add it.</p>
                                        </Message>
                                    )}
                                                {formData.approverCategoriesSingleTenancy.length > 0 && (
                                        <div style={{ marginTop: '12px' }}>
                                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {formData.approverCategoriesSingleTenancy.map(category => (
                                                                <Label key={category} color='blue' style={{ margin: 0 }}>
                                                        {category}
                                                        <Icon
                                                            name='delete'
                                                                        onClick={() => removeApproverCategorySingleTenancy(category)}
                                                            style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                        />
                                                    </Label>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Multi Tenancy Approver Categories */}
                                    <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e1e8ed', borderRadius: '4px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <Checkbox
                                                label="Enable Multi Tenancy Approver Categories"
                                                checked={formData.approverCategoriesMTEnabled}
                                                onChange={(_, { checked }) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        approverCategoriesMTEnabled: checked,
                                                        approverCategoriesMultiTenancy: checked ? prev.approverCategoriesMultiTenancy : []
                                                    }))
                                                }
                                            />
                                        </div>
                                        {formData.approverCategoriesMTEnabled && (
                                            <div style={{ marginTop: '12px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                    <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                                        Multi Tenancy Approver Categories {formData.approverCategoriesMTEnabled && <span style={{ color: '#CE1B4E' }}>*</span>}
                                                    </label>
                                                    {validMultiTenancyCategories.length > 0 && (
                                                        <Button
                                                            size='tiny'
                                                            onClick={() => {
                                                                const allSelected = validMultiTenancyCategories.every(cat => formData.approverCategoriesMultiTenancy.includes(cat));
                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    approverCategoriesMultiTenancy: allSelected ? [] : [...validMultiTenancyCategories]
                                                                }));
                                                            }}
                                                            style={{ fontSize: '0.85em' }}
                                                        >
                                                            {validMultiTenancyCategories.every(cat => formData.approverCategoriesMultiTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                                        </Button>
                                                    )}
                                                </div>
                                                {validMultiTenancyCategories.length > 0 ? (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                                        {validMultiTenancyCategories.map(category => (
                                                            <Checkbox
                                                                key={category}
                                                                label={getMTCategoryDisplayName(category)}
                                                                checked={formData.approverCategoriesMultiTenancy.includes(category)}
                                                                onChange={() => toggleApproverCategoryMultiTenancy(category)}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <Message info size="small" style={{ marginBottom: '12px' }}>
                                                        <p>No valid multi-tenancy categories available.</p>
                                                    </Message>
                                                )}
                                                {formData.approverCategoriesMultiTenancy.length > 0 && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {formData.approverCategoriesMultiTenancy.map(category => (
                                                                <Label key={category} color='purple' style={{ margin: 0 }}>
                                                                    {getMTCategoryDisplayName(category)}
                                                                    <Icon
                                                                        name='delete'
                                                                        onClick={() => removeApproverCategoryMultiTenancy(category)}
                                                                        style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                                    />
                                                                </Label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {formData.roles.includes('APPROVER') && 
                                     !formData.approverCategoriesSTEnabled && 
                                     !formData.approverCategoriesMTEnabled && (
                                        <Message warning size="small" style={{ marginTop: '12px' }}>
                                            <Message.Header>Category Required</Message.Header>
                                            <p>At least one approver category (Single Tenancy or Multi Tenancy) must be enabled. The approver will only see requests matching their assigned categories.</p>
                                        </Message>
                                    )}
                                </Form.Field>
                                <div style={{ height: '1px', background: '#e1e8ed', margin: '20px 0' }} />
                            </>
                        )}
                        {/* General Categories - ST/MT Separated based on roles */}
                        <Form.Field>
                            <label style={{ fontWeight: 600, marginBottom: '12px', display: 'block' }}>
                                General Categories
                            </label>
                            
                            {/* Single Tenancy Categories - Only shown when ST_FORM_FILLER role is enabled */}
                            {formData.roles.includes('ST_FORM_FILLER') && (
                            <div style={{ marginBottom: '20px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                        Single Tenancy Categories
                                    </label>
                                    {(availableCategories.length > 0 || formData.categoriesSingleTenancy.length > 0) && (
                                        <Button
                                            size='tiny'
                                            onClick={() => {
                                                const allCats = [...new Set([...availableCategories, ...formData.categoriesSingleTenancy])];
                                                const allSelected = allCats.every(cat => formData.categoriesSingleTenancy.includes(cat));
                                                setFormData(prev => ({
                                                    ...prev,
                                                    categoriesSingleTenancy: allSelected ? [] : allCats
                                                }));
                                            }}
                                            style={{ fontSize: '0.85em' }}
                                        >
                                            {[...new Set([...availableCategories, ...formData.categoriesSingleTenancy])].every(cat => formData.categoriesSingleTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    )}
                                </div>
                                <div style={{ marginBottom: '12px' }}>
                                    <Input
                                        placeholder='Type category and press Enter'
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter' && e.target.value.trim()) {
                                                const newCategory = e.target.value.trim();
                                                if (!formData.categoriesSingleTenancy.includes(newCategory) && !availableCategories.includes(newCategory)) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        categoriesSingleTenancy: [...prev.categoriesSingleTenancy, newCategory]
                                                    }));
                                                    setAvailableCategories(prev => [...prev, newCategory].sort());
                                                }
                                                e.target.value = '';
                                            }
                                        }}
                                        style={{ borderRadius: '6px' }}
                                    />
                                </div>
                                {(availableCategories.length > 0 || formData.categoriesSingleTenancy.length > 0) && (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                        {[...new Set([...availableCategories, ...formData.categoriesSingleTenancy])].map(category => (
                                            <Checkbox
                                                key={category}
                                                label={category}
                                                checked={formData.categoriesSingleTenancy.includes(category)}
                                                onChange={() => toggleCategorySingleTenancy(category)}
                                            />
                                        ))}
                                    </div>
                                )}
                                {formData.categoriesSingleTenancy.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {formData.categoriesSingleTenancy.map(category => (
                                                <Label key={category} color='blue' style={{ margin: 0 }}>
                                                    {category}
                                                    <Icon
                                                        name='delete'
                                                        onClick={() => removeCategorySingleTenancy(category)}
                                                        style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                    />
                                                </Label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            )}

                            {/* Multi Tenancy Categories - Only shown when MT_FORM_FILLER role is enabled */}
                            {formData.roles.includes('MT_FORM_FILLER') && (
                            <div style={{ marginBottom: '20px', padding: '16px', border: '1px solid #e1e8ed', borderRadius: '6px', background: '#f9fafb' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <label style={{ fontWeight: 600, margin: 0, color: '#062D4D' }}>
                                        Multi Tenancy Categories
                                    </label>
                                    {validMultiTenancyCategories.length > 0 && (
                                        <Button
                                            size='tiny'
                                            onClick={() => {
                                                const allSelected = validMultiTenancyCategories.every(cat => formData.categoriesMultiTenancy.includes(cat));
                                                setFormData(prev => ({
                                                    ...prev,
                                                    categoriesMultiTenancy: allSelected ? [] : [...validMultiTenancyCategories]
                                                }));
                                            }}
                                            style={{ fontSize: '0.85em' }}
                                        >
                                            {validMultiTenancyCategories.every(cat => formData.categoriesMultiTenancy.includes(cat)) ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    )}
                                </div>
                                {validMultiTenancyCategories.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                                        {validMultiTenancyCategories.map(category => (
                                            <Checkbox
                                                key={category}
                                                label={getMTCategoryDisplayName(category)}
                                                checked={formData.categoriesMultiTenancy.includes(category)}
                                                onChange={() => toggleCategoryMultiTenancy(category)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <Message info size="small" style={{ marginBottom: '12px' }}>
                                        <p>No valid multi-tenancy categories available.</p>
                                    </Message>
                                )}
                                {formData.categoriesMultiTenancy.length > 0 && (
                                    <div style={{ marginTop: '12px' }}>
                                        <strong style={{ display: 'block', marginBottom: '8px', color: '#666' }}>Selected:</strong>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {formData.categoriesMultiTenancy.map(category => (
                                                <Label key={category} color='purple' style={{ margin: 0 }}>
                                                    {getMTCategoryDisplayName(category)}
                                                    <Icon
                                                        name='delete'
                                                        onClick={() => removeCategoryMultiTenancy(category)}
                                                        style={{ cursor: 'pointer', marginLeft: '6px' }}
                                                    />
                                                </Label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                    </div>
                                )}
                            </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions style={{ borderTop: '1px solid rgba(0,0,0,0.08)', padding: '12px 20px' }}>
                    <Button onClick={() => {
                        setEditModalOpen(false);
                        setFormData(createEmptyFormData());
                        setEditingEmail('');
                        setError(null);
                    }}>
                        Cancel
                    </Button>
                    <Button primary onClick={handleUpdateUser}>
                        <Icon name='check' />
                        Update User
                    </Button>
                </Modal.Actions>
            </Modal>

            {/* Delete Confirmation */}
            <Confirm
                open={deleteConfirmOpen}
                content={`Are you sure you want to delete user "${deletingEmail}"? This action cannot be undone.`}
                confirmButton={{ content: 'Delete', negative: true }}
                cancelButton='Cancel'
                onCancel={() => {
                    setDeleteConfirmOpen(false);
                    setDeletingEmail('');
                }}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
};

export default AdminPanel;